import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid2 as Grid,
  Chip, LinearProgress, Divider, Button, CircularProgress,
  Alert, Slider, TextField, InputAdornment, Paper,
  Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import {
  TrendingUp, Store, Loyalty, People, MonetizationOn,
  CheckCircle, Star, Refresh, LocalOffer, AutoAwesome,
  EmojiEvents, Rocket, WorkspacePremium, InfoOutlined,
} from '@mui/icons-material';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CashbackComercialData {
  totals: {
    total_cashbacks: number;
    total_transacoes: number;
    total_cashback: number;
    custo_pagali: number;
    custo_comerciante: number;
    cashback_confirmado: number;
    cashback_utilizado: number;
    cashback_expirado: number;
    cashback_pendente: number;
    utilizadores_com_cashback: number;
    comerciantes_ativos: number;
    campanhas_utilizadas: number;
    avg_percentagem: number;
    avg_cashback: number;
    avg_ticket: number;
  };
  campaigns: Array<{
    id: number; name: string; tipo: string; status: string;
    funding_source: string; percentagem: number; valor_fixo: number;
    budget_total: number; budget_used: number;
    funding_split_pagali: number; funding_split_merchant: number;
    start_date: string; end_date: string;
    num_cashbacks: number; total_cashback: number;
    total_transacoes: number; utilizadores: number;
  }>;
  fundingBreakdown: Array<{
    origem_financiamento: string;
    num_cashbacks: number; total_cashback: number;
    total_transacoes: number; utilizadores: number;
  }>;
  monthly: Array<{
    mes: string; num_cashbacks: number;
    total_cashback: number; total_transacoes: number; utilizadores_unicos: number;
  }>;
  statusBreakdown: Array<{ estado: string; num: number; valor: number }>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v: number) => `${Math.round(v || 0).toLocaleString('pt-PT')}$`;
const fmtN = (v: number) => (Math.round(v || 0)).toLocaleString('pt-PT');

const FUNDING_COLORS: Record<string, string> = {
  PAGALI: '#6B2FA0',
  PARTILHADO: '#0288d1',
  COMERCIANTE: '#2e7d32',
};

const STATUS_CHIP: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  ATIVA: 'success', RASCUNHO: 'default', PAUSADA: 'warning',
  TERMINADA: 'info', ESGOTADA: 'error',
};

const PIE_COLORS = ['#6B2FA0', '#0288d1', '#2e7d32', '#f59e0b', '#e53935'];

// ─── Funding model cards data ─────────────────────────────────────────────────

const FUNDING_MODELS = [
  {
    key: 'PAGALI',
    title: 'Pagali Financia',
    subtitle: '0% custo para si',
    color: '#6B2FA0',
    bgColor: '#f3e8ff',
    icon: <AutoAwesome sx={{ fontSize: 32 }} />,
    highlight: true,
    badge: 'MAIS POPULAR',
    features: [
      'Pagali paga todo o cashback',
      'Zero risco financeiro para o comerciante',
      'Aumento imediato de tráfego na loja',
      'Acesso a clientes Pagali activos',
    ],
    costLabel: 'Custo para o comerciante',
    cost: '0 CVE',
    ideal: 'Ideal para: novos parceiros, lançamentos, promoções especiais',
  },
  {
    key: 'PARTILHADO',
    title: 'Custo Partilhado',
    subtitle: '50/50 com o Pagali',
    color: '#0288d1',
    bgColor: '#e3f2fd',
    icon: <WorkspacePremium sx={{ fontSize: 32 }} />,
    highlight: false,
    badge: 'FLEXÍVEL',
    features: [
      'Custo dividido 50/50 (configurável)',
      'Maior controlo sobre a campanha',
      'Cashback mais elevado = mais conversão',
      'Suporte prioritário de onboarding',
    ],
    costLabel: 'Custo para o comerciante',
    cost: '50% do cashback',
    ideal: 'Ideal para: parceiros activos que querem maximizar resultados',
  },
  {
    key: 'COMERCIANTE',
    title: 'Comerciante Financia',
    subtitle: 'Controlo total',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    icon: <EmojiEvents sx={{ fontSize: 32 }} />,
    highlight: false,
    badge: 'PREMIUM',
    features: [
      'Define a sua própria percentagem',
      'Total autonomia sobre o programa',
      'Personalização da campanha',
      'Relatórios exclusivos de performance',
    ],
    costLabel: 'Custo para o comerciante',
    cost: '100% do cashback',
    ideal: 'Ideal para: grandes comerciantes com estratégia própria de fidelização',
  },
];

// ─── ROI Simulator ─────────────────────────────────────────────────────────────

const FUNDING_MERCHANT_SHARE: Record<string, number> = {
  PAGALI: 0,
  PARTILHADO: 0.5,
  COMERCIANTE: 1,
};

interface SimulatorResult {
  receitaAtual: number;
  custoCashback: number;
  custoComercianteMes: number;
  clientesAdicionaisEstimados: number;
  receitaAdicional: number;
  lucroLiquido: number;
  roi: number;
  paybackDias: number;
}

function calcularROI(
  clientesMes: number,
  ticketMedio: number,
  taxaCashback: number,
  fundingModel: string,
): SimulatorResult {
  const receitaAtual = clientesMes * ticketMedio;
  const custoCashback = receitaAtual * (taxaCashback / 100);
  const custoComercianteMes = custoCashback * FUNDING_MERCHANT_SHARE[fundingModel];
  // Clientes com cashback voltam 35% mais e trazem 20% novos clientes via word-of-mouth
  const clientesRetencaoExtra = clientesMes * 0.35;
  const clientesNovos = clientesMes * 0.20;
  const clientesAdicionaisEstimados = Math.round(clientesRetencaoExtra + clientesNovos);
  // Clientes com cashback gastam em média 15% mais por visita
  const receitaAdicional = (clientesRetencaoExtra + clientesNovos) * ticketMedio * 1.15;
  const lucroLiquido = receitaAdicional - custoComercianteMes;
  const roi = custoComercianteMes > 0 ? (lucroLiquido / custoComercianteMes) * 100 : Infinity;
  const paybackDias = custoComercianteMes > 0 ? Math.ceil((custoComercianteMes / receitaAdicional) * 30) : 0;
  return { receitaAtual, custoCashback, custoComercianteMes, clientesAdicionaisEstimados, receitaAdicional, lucroLiquido, roi, paybackDias };
}

// ─── Main component ────────────────────────────────────────────────────────────

const CashbackComercial: React.FC = () => {
  const [data, setData] = useState<CashbackComercialData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Simulator state
  const [clientesMes, setClientesMes] = useState(500);
  const [ticketMedio, setTicketMedio] = useState(1500);
  const [taxaCashback, setTaxaCashback] = useState(5);
  const [fundingModel, setFundingModel] = useState('PAGALI');

  const roi = useMemo(
    () => calcularROI(clientesMes, ticketMedio, taxaCashback, fundingModel),
    [clientesMes, ticketMedio, taxaCashback, fundingModel],
  );

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await api.get<{ success: boolean; data: CashbackComercialData }>('/admin/reports/cashback-comercial');
      if (r.data.success) setData(r.data.data);
    } catch {
      setError('Erro ao carregar dados. Verifique a ligação e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const roiChartData = useMemo(() => [
    { label: 'Receita Actual', valor: roi.receitaAtual, fill: '#94a3b8' },
    { label: 'Receita c/ Cashback', valor: roi.receitaAtual + roi.receitaAdicional, fill: '#6B2FA0' },
  ], [roi]);

  const fundingPieData = useMemo(() => {
    if (!data) return [];
    return data.fundingBreakdown.map(f => ({
      name: f.origem_financiamento === 'PAGALI' ? 'Pagali Financia'
        : f.origem_financiamento === 'PARTILHADO' ? 'Partilhado'
        : 'Comerciante',
      value: Number(f.total_cashback),
      color: FUNDING_COLORS[f.origem_financiamento] || '#9e9e9e',
    })).filter(d => d.value > 0);
  }, [data]);

  const monthlyCombined = useMemo(() => {
    if (!data?.monthly.length) return [];
    return data.monthly.map(m => ({
      ...m,
      label: m.mes,
    }));
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const t = data?.totals;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', pb: 6 }}>

      {/* ── HERO BANNER ────────────────────────────────────────────── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a0533 0%, #3b1278 50%, #6B2FA0 100%)',
        borderRadius: 3, p: { xs: 3, md: 5 }, mb: 4, color: '#fff', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -60, right: 80, width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.03)' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Chip label="PROGRAMA COMERCIAL" size="small" sx={{ bgcolor: '#f59e0b', color: '#000', fontWeight: 700, mb: 2, fontSize: 11 }} />
            <Typography variant="h4" fontWeight={800} sx={{ mb: 1, lineHeight: 1.2 }}>
              Programa de Cashback Pagali
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 400, maxWidth: 600 }}>
              Fidelize clientes, aumente as vendas e faça crescer o seu negócio —<br />
              sem custos adicionais com o modelo <strong>Pagali Financia</strong>.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Actualizar
          </Button>
        </Box>

        {/* Hero KPIs */}
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {[
            { label: 'Utilizadores Beneficiados', value: fmtN(t?.utilizadores_com_cashback || 0), icon: <People /> },
            { label: 'Total Cashback Distribuído', value: fmt(t?.total_cashback || 0), icon: <MonetizationOn /> },
            { label: 'Campanhas Activas na Plataforma', value: fmtN(data?.campaigns.filter(c => c.status === 'ATIVA').length || 0), icon: <LocalOffer /> },
            { label: 'Comerciantes no Programa', value: fmtN(t?.comerciantes_ativos || 0), icon: <Store /> },
          ].map((k, i) => (
            <Grid key={i} size={{ xs: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)' }}>
                <Box sx={{ color: '#f59e0b', mb: 1 }}>{k.icon}</Box>
                <Typography variant="h5" fontWeight={800}>{k.value}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{k.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── MODELOS DE FINANCIAMENTO ───────────────────────────────── */}
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Modelos de Financiamento</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Escolha como quer participar no programa. Pode começar sem custo algum.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 5 }}>
        {FUNDING_MODELS.map((m) => (
          <Grid key={m.key} size={{ xs: 12, md: 4 }}>
            <Card elevation={m.highlight ? 4 : 1} sx={{
              height: '100%', border: `2px solid ${m.highlight ? m.color : 'transparent'}`,
              position: 'relative', overflow: 'visible',
            }}>
              {m.badge && (
                <Chip label={m.badge} size="small" sx={{
                  position: 'absolute', top: -12, left: 20,
                  bgcolor: m.color, color: '#fff', fontWeight: 700, fontSize: 10,
                }} />
              )}
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ color: m.color, bgcolor: m.bgColor, p: 1.5, borderRadius: 2, display: 'flex' }}>
                    {m.icon}
                  </Box>
                  <Box>
                    <Typography fontWeight={700} variant="h6">{m.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{m.subtitle}</Typography>
                  </Box>
                </Box>

                <Box sx={{ bgcolor: m.bgColor, borderRadius: 2, p: 2, mb: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{m.costLabel}</Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: m.color }}>{m.cost}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  {m.features.map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                      <CheckCircle sx={{ fontSize: 16, color: m.color, mt: 0.3, flexShrink: 0 }} />
                      <Typography variant="body2">{f}</Typography>
                    </Box>
                  ))}
                </Box>

                <Typography variant="caption" color="text.secondary" fontStyle="italic">{m.ideal}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── ROI SIMULATOR ──────────────────────────────────────────── */}
      <Box sx={{ bgcolor: 'action.hover', borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, md: 4 }, mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Rocket sx={{ color: '#6B2FA0' }} />
          <Typography variant="h5" fontWeight={700}>Simulador de Retorno (ROI)</Typography>
          <Tooltip title="Estimativas baseadas em benchmarks do sector de fidelização: retenção +35%, novos clientes via referência +20%, ticket médio +15%">
            <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'pointer' }} />
          </Tooltip>
        </Box>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Insira os dados do seu negócio e veja o retorno estimado do programa de cashback.
          <Chip label="Estimativas baseadas em benchmarks de fidelização" size="small" sx={{ ml: 1, fontSize: 11 }} />
        </Typography>

        <Grid container spacing={4}>
          {/* Inputs */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Clientes por mês: <strong>{fmtN(clientesMes)}</strong>
              </Typography>
              <Slider
                value={clientesMes}
                onChange={(_, v) => setClientesMes(v as number)}
                min={50} max={5000} step={50}
                sx={{ color: '#6B2FA0' }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">50</Typography>
                <Typography variant="caption" color="text.secondary">5.000</Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Ticket médio: <strong>{fmt(ticketMedio)}</strong>
              </Typography>
              <Slider
                value={ticketMedio}
                onChange={(_, v) => setTicketMedio(v as number)}
                min={200} max={20000} step={100}
                sx={{ color: '#6B2FA0' }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">200$</Typography>
                <Typography variant="caption" color="text.secondary">20.000$</Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Taxa de cashback: <strong>{taxaCashback}%</strong>
              </Typography>
              <Slider
                value={taxaCashback}
                onChange={(_, v) => setTaxaCashback(v as number)}
                min={1} max={15} step={0.5}
                marks={[{ value: 5, label: '5%' }, { value: 10, label: '10%' }]}
                sx={{ color: '#6B2FA0' }}
              />
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Modelo de financiamento</Typography>
              <ToggleButtonGroup
                value={fundingModel}
                exclusive
                onChange={(_, v) => v && setFundingModel(v)}
                size="small"
              >
                <ToggleButton value="PAGALI" sx={{ '&.Mui-selected': { bgcolor: '#f3e8ff', borderColor: '#6B2FA0', color: '#6B2FA0' } }}>
                  Pagali Financia
                </ToggleButton>
                <ToggleButton value="PARTILHADO" sx={{ '&.Mui-selected': { bgcolor: '#e3f2fd', borderColor: '#0288d1', color: '#0288d1' } }}>
                  Partilhado
                </ToggleButton>
                <ToggleButton value="COMERCIANTE" sx={{ '&.Mui-selected': { bgcolor: '#e8f5e9', borderColor: '#2e7d32', color: '#2e7d32' } }}>
                  Comerciante
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>

          {/* Results */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: '#6B2FA0' }}>
                Resultados Estimados por Mês
              </Typography>

              {[
                { label: 'Receita actual', value: fmt(roi.receitaAtual), color: '#64748b' },
                { label: 'Custo cashback total', value: fmt(roi.custoCashback), color: '#f59e0b', sub: `Custo para si: ${fmt(roi.custoComercianteMes)}` },
                { label: 'Clientes adicionais estimados', value: `+${fmtN(roi.clientesAdicionaisEstimados)}`, color: '#0288d1' },
                { label: 'Receita adicional estimada', value: fmt(roi.receitaAdicional), color: '#2e7d32' },
              ].map((row, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="body2">{row.label}</Typography>
                    {row.sub && <Typography variant="caption" color="text.secondary">{row.sub}</Typography>}
                  </Box>
                  <Typography fontWeight={700} sx={{ color: row.color }}>{row.value}</Typography>
                </Box>
              ))}

              <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: roi.lucroLiquido >= 0 ? '#e8f5e9' : '#fce4ec', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Lucro líquido estimado</Typography>
                <Typography variant="h4" fontWeight={900} sx={{ color: roi.lucroLiquido >= 0 ? '#2e7d32' : '#c62828' }}>
                  {fmt(roi.lucroLiquido)}
                </Typography>
                {roi.roi !== Infinity && (
                  <Chip
                    label={`ROI: ${Math.round(roi.roi)}%`}
                    color={roi.roi >= 0 ? 'success' : 'error'}
                    sx={{ mt: 1, fontWeight: 700, fontSize: 13 }}
                  />
                )}
                {roi.roi === Infinity && (
                  <Chip label="ROI: ∞ — sem custo para si" color="success" sx={{ mt: 1, fontWeight: 700 }} />
                )}
              </Box>

              {/* Mini bar chart */}
              <Box sx={{ mt: 2, height: 120, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={roiChartData} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
                    <Bar dataKey="valor" radius={[0, 4, 4, 0]}>
                      {roiChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                    <RechartsTooltip formatter={(v: any) => [fmt(v), 'Valor']} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── COMO FUNCIONA ──────────────────────────────────────────── */}
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Como Funciona</Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {[
          {
            step: '01', icon: <Store sx={{ fontSize: 36, color: '#6B2FA0' }} />,
            title: 'Cliente compra no seu negócio',
            desc: 'O cliente paga com Pagali. O sistema regista automaticamente a transação e calcula o cashback elegível.',
          },
          {
            step: '02', icon: <Loyalty sx={{ fontSize: 36, color: '#f59e0b' }} />,
            title: 'Cashback creditado na carteira',
            desc: 'O cashback é creditado imediatamente na carteira Pagali do cliente. Ele recebe uma notificação push.',
          },
          {
            step: '03', icon: <TrendingUp sx={{ fontSize: 36, color: '#2e7d32' }} />,
            title: 'Cliente volta para gastar o cashback',
            desc: 'O saldo de cashback incentiva a próxima visita. Clientes com cashback regressam 35% mais frequentemente.',
          },
        ].map((s, i) => (
          <Grid key={i} size={{ xs: 12, md: 4 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography sx={{ fontSize: 36, fontWeight: 900, color: '#e9e0f7', lineHeight: 1 }}>{s.step}</Typography>
                  {s.icon}
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{s.title}</Typography>
                <Typography variant="body2" color="text.secondary">{s.desc}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── VANTAGENS PARA COMERCIANTES ─────────────────────────────── */}
      <Box sx={{ bgcolor: '#1a0533', borderRadius: 3, p: { xs: 2, md: 4 }, mb: 5, color: '#fff' }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          <Star sx={{ color: '#f59e0b', mr: 1, verticalAlign: 'middle' }} />
          Vantagens para o seu Negócio
        </Typography>
        <Grid container spacing={3}>
          {[
            { icon: <People />, title: '+35% Taxa de Retenção', desc: 'Clientes com cashback voltam significativamente mais vezes. Cada visita gera nova receita com custo de aquisição zero.', color: '#a78bfa' },
            { icon: <TrendingUp />, title: '+15% Ticket Médio', desc: 'Saber que vão receber cashback leva os clientes a gastar mais por visita. A perceção de valor melhora a decisão de compra.', color: '#67e8f9' },
            { icon: <People />, title: '+20% Novos Clientes', desc: 'O efeito viral do cashback: os seus clientes partilham a experiência e trazem novos clientes ao negócio.', color: '#86efac' },
            { icon: <MonetizationOn />, title: 'Dados de Compra em Tempo Real', desc: 'Acesso a relatórios detalhados sobre o comportamento dos seus clientes, tendências de compra e performance das campanhas.', color: '#fde68a' },
            { icon: <AutoAwesome />, title: 'Integração em Minutos', desc: 'Já aceita Pagali? Aderir ao programa de cashback é imediato — não precisa de nova integração técnica.', color: '#f9a8d4' },
            { icon: <EmojiEvents />, title: 'Campanhas Personalizadas', desc: 'Crie cashback especial para épocas festivas, lançamentos ou para categorias de produto específicas.', color: '#c4b5fd' },
          ].map((v, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ color: v.color, flexShrink: 0, mt: 0.5 }}>{v.icon}</Box>
                <Box>
                  <Typography fontWeight={700} sx={{ mb: 0.5 }}>{v.title}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>{v.desc}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── PERFORMANCE DA PLATAFORMA ──────────────────────────────── */}
      {data && (data.campaigns.length > 0 || data.monthly.length > 0) && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Performance da Plataforma</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>Dados reais do programa Pagali Cashback.</Typography>

          <Grid container spacing={3}>
            {/* Funding breakdown pie */}
            {fundingPieData.length > 0 && (
              <Grid size={{ xs: 12, md: 5 }}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Cashback por Modelo de Financiamento</Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={fundingPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${Math.round((percent || 0) * 100)}%`}>
                          {fundingPieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <RechartsTooltip formatter={(v: any) => [fmt(v), 'Cashback']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Monthly trend */}
            {monthlyCombined.length > 0 && (
              <Grid size={{ xs: 12, md: fundingPieData.length > 0 ? 7 : 12 }}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Evolução Mensal do Cashback</Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={monthlyCombined}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RechartsTooltip formatter={(v: any, name: any) => [
                          name === 'total_cashback' ? fmt(v) : fmtN(v),
                          name === 'total_cashback' ? 'Cashback' : 'Utilizadores',
                        ]} />
                        <Legend />
                        <Area type="monotone" dataKey="total_cashback" name="total_cashback" stroke="#6B2FA0" fill="#f3e8ff" strokeWidth={2} />
                        <Area type="monotone" dataKey="utilizadores_unicos" name="utilizadores_unicos" stroke="#0288d1" fill="#e3f2fd" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Campaign table */}
            {data.campaigns.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Campanhas na Plataforma</Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Financiamento</TableCell>
                            <TableCell align="right">Cashback</TableCell>
                            <TableCell align="right">Budget</TableCell>
                            <TableCell align="right">Utilizadores</TableCell>
                            <TableCell>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.campaigns.map((c) => (
                            <TableRow key={c.id} hover>
                              <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                              <TableCell>
                                <Chip label={c.tipo} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={c.funding_source}
                                  size="small"
                                  sx={{ bgcolor: `${FUNDING_COLORS[c.funding_source]}18`, color: FUNDING_COLORS[c.funding_source], fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell align="right">{c.percentagem > 0 ? `${c.percentagem}%` : fmt(c.valor_fixo)}</TableCell>
                              <TableCell align="right">
                                <Box>
                                  <Typography variant="body2">{fmt(c.budget_used)} / {fmt(c.budget_total)}</Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={c.budget_total > 0 ? Math.min(100, (c.budget_used / c.budget_total) * 100) : 0}
                                    sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: 'action.selected', '& .MuiLinearProgress-bar': { bgcolor: FUNDING_COLORS[c.funding_source] || '#6B2FA0' } }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="right">{fmtN(c.utilizadores)}</TableCell>
                              <TableCell>
                                <Chip label={c.status} color={STATUS_CHIP[c.status] || 'default'} size="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        borderRadius: 3, p: { xs: 3, md: 5 }, textAlign: 'center', color: '#000',
      }}>
        <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
          Pronto para começar a crescer?
        </Typography>
        <Typography sx={{ mb: 3, opacity: 0.8, maxWidth: 500, mx: 'auto' }}>
          Adesão gratuita ao programa. A equipa Pagali configura a sua primeira campanha sem custo.
          Resultados visíveis a partir do primeiro mês.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            sx={{ bgcolor: '#1a0533', color: '#fff', fontWeight: 700, px: 4, '&:hover': { bgcolor: '#3b1278' } }}
            href="mailto:comercial@pagali.cv?subject=Adesão ao Programa Cashback"
          >
            Contactar Equipa Comercial
          </Button>
          <Button
            variant="outlined"
            size="large"
            sx={{ borderColor: '#1a0533', color: '#1a0533', fontWeight: 700, px: 4 }}
            onClick={() => window.open('/cashback', '_self')}
          >
            Gerir Campanhas
          </Button>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.6 }}>
          comercial@pagali.cv · +238 260 00 00
        </Typography>
      </Box>

    </Box>
  );
};

export default CashbackComercial;
